/**
 * ValueAddedServices Component
 * Displays additional financial services for clients in the sidebar
 * Locked for free (Plus) plan users with upgrade prompt
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaBrain, FaChild, FaRocket, FaBriefcase, FaCreditCard, 
  FaBuilding, FaHeartbeat, FaShieldAlt, FaUmbrellaBeach,
  FaUserGraduate, FaWallet, FaChartPie, FaHandPointRight,
  FaCheck, FaSpinner, FaLock, FaArrowRight
} from 'react-icons/fa';
import axios from 'axios';
import UpgradeModal from '../UpgradeModal';
import { ModeBadge } from '../shared';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Service definitions
const services = [
  {
    id: 'money-personality',
    icon: FaBrain,
    title: 'My Money Personality Test',
    description: 'Discover your unique money personality through a quick AI-powered test. Designed for Salaried Professionals, Business Owners and Others — receive a personalised report on your money behaviour, strengths, blind spots and next steps.',
    color: 'purple',
    mode: 'diy'
  },
  {
    id: 'child-future-ready',
    icon: FaChild,
    title: 'Your Child Future Ready?',
    description: 'A guided assessment to know whether your child\'s future financial needs are adequately planned for. Click to take the test.',
    color: 'cyan',
    actionType: 'navigate',
    actionPath: '/dashboard/child-future-ready',
    mode: 'diy',
    alwaysAvailable: true
  },
  {
    id: 'shiksha-sankalp',
    icon: FaChild,
    title: 'Shiksha Sankalp',
    description: '360 Degree Child Education Goal Planning with Report, powered by AI.',
    color: 'blue',
    mode: 'guided'
  },
  {
    id: 'goal-manthan',
    icon: FaRocket,
    title: 'Goal Manthan',
    description: 'Career Planning with Education Goal Planning for 6th to 12th Class Students with Report.',
    color: 'green',
    mode: 'guided'
  },
  {
    id: 'pathfinder',
    icon: FaBriefcase,
    title: 'Pathfinder',
    description: 'Free Career & Job Switch Test for Working Professionals or Job Seekers with Report.',
    color: 'orange',
    mode: 'guided'
  },
  {
    id: 'debt-detox',
    icon: FaCreditCard,
    title: 'Debt Detox 360',
    description: 'Free debt-planning consultation. We review your loans, EMIs and credit-card debt and design a clear, tax-efficient roadmap to become debt-free faster, with a detailed report.',
    color: 'red',
    mode: 'guided'
  },
  {
    id: 'business-risk',
    icon: FaBuilding,
    title: 'Business Risk Assessment Test',
    description: 'A comprehensive risk-assessment test for business owners covering business continuity, key-person dependence, liability and succession. Includes a confidential report with advisor follow-up.',
    color: 'indigo',
    mode: 'guided'
  },
  {
    id: 'msme-risk-mri',
    icon: FaShieldAlt,
    title: 'MSME Risk MRI',
    description: 'An MRI-style risk scan for MSMEs. Identifies the top financial and operational risks your business carries and the right way to mitigate them.',
    color: 'teal',
    mode: 'guided'
  },
  // Portfolio Review Services
  {
    id: 'insurance-portfolio',
    icon: FaShieldAlt,
    title: 'Insurance Portfolio Review',
    description: 'Comprehensive expert review of your life, health and general insurance portfolio. Includes adequacy-gap analysis, premium leakage, claim-readiness and right-fit recommendations.',
    color: 'teal',
    category: 'portfolio',
    mode: 'guided'
  },
  {
    id: 'retirement-portfolio',
    icon: FaUmbrellaBeach,
    title: 'Retirement Portfolio Review',
    description: 'Expert review of your retirement corpus, pension plans and withdrawal strategy. Get a clear answer to: "Will my money last my lifetime?" with actionable rebalancing inputs.',
    color: 'amber',
    category: 'portfolio',
    mode: 'guided'
  },
  {
    id: 'child-education-portfolio',
    icon: FaUserGraduate,
    title: 'Child Education Portfolio Review',
    description: 'A focused review of your existing education savings plans and fund performance, with a clear goal-achievement tracker and rebalancing suggestions to keep your child\'s dreams on track.',
    color: 'cyan',
    category: 'portfolio',
    mode: 'guided'
  },
  {
    id: 'wealth-portfolio',
    icon: FaWallet,
    title: 'Wealth Portfolio Review',
    description: 'A complete wealth-management and asset-allocation review covering equity, debt, gold and alternatives — with recommendations to optimise returns aligned to your risk profile and goals.',
    color: 'gold',
    category: 'portfolio',
    mode: 'guided'
  },
  {
    id: 'mf-portfolio',
    icon: FaChartPie,
    title: 'Mutual Fund Portfolio Review',
    description: 'Detailed performance analysis of your mutual fund investments with category benchmarking, overlap analysis and clear rebalancing recommendations from our expert advisors.',
    color: 'emerald',
    category: 'portfolio',
    mode: 'guided'
  }
];

// Color mapping for service icons
const colorClasses = {
  purple: 'text-purple-400 bg-purple-500/20',
  blue: 'text-blue-400 bg-blue-500/20',
  green: 'text-green-400 bg-green-500/20',
  orange: 'text-orange-400 bg-orange-500/20',
  red: 'text-red-400 bg-red-500/20',
  indigo: 'text-indigo-400 bg-indigo-500/20',
  pink: 'text-pink-400 bg-pink-500/20',
  teal: 'text-teal-400 bg-teal-500/20',
  amber: 'text-amber-400 bg-amber-500/20',
  cyan: 'text-cyan-400 bg-cyan-500/20',
  gold: 'text-yellow-400 bg-yellow-500/20',
  emerald: 'text-emerald-400 bg-emerald-500/20'
};

// Individual service item component
const ServiceItem = ({ service, isExpanded, onToggle, onShowInterest, onNavigate, hasSentInterest, isSending }) => {
  const Icon = service.icon;
  const isNavigateAction = service.actionType === 'navigate';
  
  return (
    <div className="rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center space-x-2 px-3 py-2 text-left hover:bg-[#1E3A5F]/50 transition-all rounded-lg"
      >
        <div className={`w-7 h-7 rounded-md ${colorClasses[service.color]} flex items-center justify-center flex-shrink-0`}>
          <Icon size={14} />
        </div>
        <span className="text-gray-300 text-sm flex-1 truncate">{service.title}</span>
        {service.mode && <ModeBadge mode={service.mode} size="xs" />}
        <FaHandPointRight className={`text-gray-500 text-xs transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
      </button>
      
      {isExpanded && (
        <div className="px-3 pb-3 pt-2 ml-9 border-l-2 border-[#FFD700]/30 bg-[#0F1B30]/40 rounded-r-md">
          {/* Full title in bold so users can read the full service name even when the
              row label is truncated. */}
          <div className="flex items-start gap-2 mb-1.5">
            <p className="text-white text-sm font-bold leading-snug flex-1">{service.title}</p>
            {service.mode && <ModeBadge mode={service.mode} size="xs" />}
          </div>
          <p className="text-gray-400 text-xs leading-relaxed mb-3">{service.description}</p>
          {isNavigateAction ? (
            <button
              onClick={() => onNavigate(service.actionPath)}
              data-testid={`open-${service.id}-btn`}
              className="px-3 py-1.5 bg-cyan-500/20 text-cyan-300 text-xs font-medium rounded-md hover:bg-cyan-500/30 transition-all flex items-center space-x-1"
            >
              <FaArrowRight size={10} />
              <span>Take the Test</span>
            </button>
          ) : hasSentInterest ? (
            <div className="flex items-center space-x-1 text-green-400 text-xs">
              <FaCheck size={10} />
              <span>Interest sent! Your advisor will connect soon.</span>
            </div>
          ) : (
            <button
              onClick={() => onShowInterest(service.id, service.title)}
              disabled={isSending}
              className="px-3 py-1.5 bg-[#FFD700]/20 text-[#FFD700] text-xs font-medium rounded-md hover:bg-[#FFD700]/30 transition-all disabled:opacity-50 flex items-center space-x-1"
            >
              {isSending ? (
                <>
                  <FaSpinner className="animate-spin" size={10} />
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <FaHandPointRight size={10} />
                  <span>Show Interest</span>
                </>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Locked service item for free users
const LockedServiceItem = ({ service, onUpgradeClick }) => {
  const Icon = service.icon;
  return (
    <button
      onClick={onUpgradeClick}
      className="w-full flex items-center space-x-2 px-3 py-2 text-left hover:bg-[#1E3A5F]/30 transition-all rounded-lg group"
    >
      <div className={`w-7 h-7 rounded-md bg-gray-700/30 flex items-center justify-center flex-shrink-0`}>
        <Icon size={14} className="text-gray-600" />
      </div>
      <span className="text-gray-600 text-sm flex-1 truncate">{service.title}</span>
      {service.mode && <ModeBadge mode={service.mode} size="xs" />}
      <FaLock size={10} className="text-gray-600 group-hover:text-[#FFD700] transition-colors" />
    </button>
  );
};

const ValueAddedServices = ({ isFreePlan = false }) => {
  const navigate = useNavigate();
  const [expandedService, setExpandedService] = useState(null);
  const [interestSent, setInterestSent] = useState({});
  const [sending, setSending] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const handleShowInterest = async (serviceId, serviceTitle) => {
    setSending(serviceId);
    try {
      await axios.post(`${API}/client/service-interest`, {
        service_id: serviceId,
        service_title: serviceTitle
      });
      setInterestSent(prev => ({ ...prev, [serviceId]: true }));
    } catch (error) {
    } finally {
      setSending(null);
    }
  };

  const handleToggle = (serviceId) => {
    setExpandedService(expandedService === serviceId ? null : serviceId);
  };

  const handleNavigate = (path) => {
    setExpandedService(null);
    navigate(path);
  };

  // Separate services by category
  const regularServices = services.filter(s => !s.category);
  const portfolioServices = services.filter(s => s.category === 'portfolio');

  return (
    <div className="mt-6 pt-4 border-t border-[#2D5A8F]">
      <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3 px-2">
        Premium Unique Services
        {isFreePlan && <FaLock className="inline ml-2 text-gray-600" size={10} />}
      </p>
      <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1 custom-scrollbar">
        {/* Regular Services */}
        {regularServices.map(service => (
          (isFreePlan && !service.alwaysAvailable) ? (
            <LockedServiceItem
              key={service.id}
              service={service}
              onUpgradeClick={() => setShowUpgradeModal(true)}
            />
          ) : (
            <ServiceItem
              key={service.id}
              service={service}
              isExpanded={expandedService === service.id}
              onToggle={() => handleToggle(service.id)}
              onShowInterest={handleShowInterest}
              onNavigate={handleNavigate}
              hasSentInterest={interestSent[service.id]}
              isSending={sending === service.id}
            />
          )
        ))}
        
        {/* Portfolio Review Services Section */}
        <div className="pt-3 mt-2 border-t border-[#2D5A8F]/50">
          <p className="text-[#FFD700] text-[10px] font-semibold uppercase tracking-wider mb-2 px-2 flex items-center">
            <FaChartPie className="mr-1" size={10} />
            Expert Level Services
            {isFreePlan && <FaLock className="ml-2 text-gray-600" size={9} />}
          </p>
          {portfolioServices.map(service => (
            isFreePlan ? (
              <LockedServiceItem
                key={service.id}
                service={service}
                onUpgradeClick={() => setShowUpgradeModal(true)}
              />
            ) : (
              <ServiceItem
                key={service.id}
                service={service}
                isExpanded={expandedService === service.id}
                onToggle={() => handleToggle(service.id)}
                onShowInterest={handleShowInterest}
                onNavigate={handleNavigate}
                hasSentInterest={interestSent[service.id]}
                isSending={sending === service.id}
              />
            )
          ))}
        </div>
      </div>
      {isFreePlan ? (
        <p className="text-gray-600 text-[10px] px-2 mt-2 italic">
          Upgrade to PRO+ to access premium unique services &amp; expert level services.
        </p>
      ) : (
        <p className="text-gray-500 text-[10px] px-2 mt-2 italic">
          Click on a service and show interest; your financial advisor will connect with more details.
        </p>
      )}

      <UpgradeModal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} />
    </div>
  );
};

export default ValueAddedServices;
